/**
 * useTrustedShareNotifier — sends a courtesy "your member just shared a
 * pre-meet checklist" email to every Trusted Circle contact with a usable
 * email address.
 *
 * Implementation: invokes the `send-trusted-share` Supabase edge function,
 * which is JWT-verified, RLS-scopes the checklist + contacts to the caller,
 * sends each email via Resend (using `RESEND_API_KEY` from the project's
 * function secrets), and audits each attempt to `trusted_share_outbox`.
 *
 * Failures never block the wizard save flow — the toast surfaces the
 * outcome and the function call returns.
 */
import { useCallback } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useTrustedCircle } from "@/lib/safety/useTrustedCircle";
import type { PreMeetChecklist } from "@/lib/safety/usePreMeet";

interface NotifyResult {
  attempted: number;
  sent: number;
  failed: number;
  skipped: number;
  error: string | null;
}

interface FunctionResponse {
  attempted?: number;
  sent?: number;
  failed?: number;
  skipped?: number;
  error?: string;
}

export function useTrustedShareNotifier() {
  const { user } = useAuth();
  const { contacts } = useTrustedCircle();

  const notify = useCallback(
    async (
      checklist: PreMeetChecklist,
      opts?: { silent?: boolean; contactIds?: string[] },
    ): Promise<NotifyResult> => {
      const result: NotifyResult = {
        attempted: 0,
        sent: 0,
        failed: 0,
        skipped: 0,
        error: null,
      };

      if (!user) {
        result.error = "not_signed_in";
        if (!opts?.silent) toast.error("Sign in to share with your Trusted Circle.");
        return result;
      }
      if (contacts.length === 0) {
        if (!opts?.silent) toast.info("No Trusted Circle contacts yet.");
        return result;
      }

      const { data, error } = await supabase.functions.invoke<FunctionResponse>(
        "send-trusted-share",
        {
          body: {
            checklist_id: checklist.id,
            ...(opts?.contactIds && opts.contactIds.length > 0
              ? { contact_ids: opts.contactIds }
              : {}),
          },
        },
      );

      if (error) {
        result.error = error.message;
        if (!opts?.silent) {
          toast.error(`Couldn't send trusted-share emails: ${error.message}`);
        }
        return result;
      }
      if (data?.error) {
        result.error = data.error;
        if (!opts?.silent) {
          toast.error(`Couldn't send trusted-share emails: ${data.error}`);
        }
        return result;
      }

      result.attempted = data?.attempted ?? 0;
      result.sent = data?.sent ?? 0;
      result.failed = data?.failed ?? 0;
      result.skipped = data?.skipped ?? 0;

      if (!opts?.silent) {
        if (result.sent > 0 && result.failed === 0) {
          toast.success(
            `Sent ${result.sent} trusted-share email${result.sent === 1 ? "" : "s"}.`,
          );
        } else if (result.sent > 0 && result.failed > 0) {
          toast.warning(
            `Sent ${result.sent}, ${result.failed} failed. Check your contacts and retry.`,
          );
        } else if (result.sent === 0 && result.skipped > 0) {
          toast.warning(
            `No emails sent — ${result.skipped} contact${result.skipped === 1 ? "" : "s"} missing an email address.`,
          );
        } else if (result.failed > 0) {
          toast.error(
            `All ${result.failed} send${result.failed === 1 ? "" : "s"} failed.`,
          );
        } else {
          toast.info("Nothing to send.");
        }
      }
      return result;
    },
    [user, contacts],
  );

  return { notify, contactCount: contacts.length };
}
